import React, { useMemo, useState } from 'react';
import { Drawer, SIZE as DRAWER_SIZE } from 'baseui/drawer';
import { Button, KIND, SIZE } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Select } from 'baseui/select';
import { HeadingMedium } from 'baseui/typography';
import { styled } from 'styletron-react';
import { useCreateUserEstate } from '../hooks/useUserEstate';
import { useEstates } from '../hooks/useEstates';
import { useUserTypes } from '../hooks/useUserTypes';
import { useEstateGroupsForHome } from '../hooks/useHomeConfig';
import { useGetBuilding } from '../api/generated';
import { DateField } from './ui/DateTimeField';

const DrawerComponent = Drawer as any;
const SelectComponent = Select as any;
const FormControlComponent = FormControl as any;

// Lift the Select popover above the drawer (drawer Root uses zIndex 10000)
const selectPopoverOverrides = {
  Popover: {
    props: {
      overrides: {
        Body: { style: { zIndex: 10010 } },
      },
    },
  },
};

const Body = styled('div', {
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const Footer = styled('div', {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  padding: '16px 24px',
  borderTop: '1px solid #E5E7EB',
});

interface AddUserEstateDrawerProps {
  isOpen: boolean;
  userId: number;
  onClose: () => void;
}

// Status fallbacks that match the user_estate_status table.
// 1 = Pending (default for "request"-style adds), 2 = Approved.
const STATUS_OPTIONS = [
  { id: 1, label: 'Pending' },
  { id: 2, label: 'Approved' },
];

export const AddUserEstateDrawer: React.FC<AddUserEstateDrawerProps> = ({
  isOpen,
  userId,
  onClose,
}) => {
  const createMutation = useCreateUserEstate();

  const [selectedEstateGroup, setSelectedEstateGroup] = useState<{ id: number; label: string } | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<{ id: number; label: string } | null>(null);
  const [estateSearch, setEstateSearch] = useState('');

  const { estateGroups, isLoading: estateGroupsLoading } = useEstateGroupsForHome();

  const { data: buildingsResponse, isLoading: buildingsLoading } = useGetBuilding(
    {
      limit: 200,
      estate_group_id: selectedEstateGroup?.id,
      order_by: 'name',
      order_direction: 'ASC',
    } as any,
    { query: { enabled: !!selectedEstateGroup?.id } } as any,
  );
  const buildings: Array<{ id: number; name: string }> =
    (buildingsResponse?.data as any)?.data || (buildingsResponse?.data as any) || [];

  const { estates: estatesData, isLoading: estatesLoading } = useEstates({
    name: estateSearch,
    estateGroupId: selectedEstateGroup?.id,
    buildingId: selectedBuilding?.id,
    limit: 50,
  });
  const { userTypes, isLoading: userTypesLoading } = useUserTypes();

  const [selectedEstate, setSelectedEstate] = useState<{ id: number; label: string } | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<{ id: number; label: string } | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<{ id: number; label: string }>(STATUS_OPTIONS[1]); // Approved by default — admin add is direct
  const [expirationDate, setExpirationDate] = useState<string>('');

  const estateGroupOptions = useMemo(
    () => (estateGroups || []).map((g: any) => ({
      id: g.id as number,
      label: g.name as string,
    })),
    [estateGroups],
  );

  const buildingOptions = useMemo(
    () => (buildings || []).map((b: any) => ({
      id: b.id as number,
      label: b.name as string,
    })),
    [buildings],
  );

  const estateOptions = useMemo(
    () => (estatesData || []).map((e: any) => ({
      id: e.id as number,
      label: e.name as string,
    })),
    [estatesData],
  );

  const userTypeOptions = useMemo(
    () => (userTypes || []).map((t: any) => ({
      id: t.id as number,
      label: (t.name as string) || `Type ${t.id}`,
    })),
    [userTypes],
  );

  const reset = () => {
    setSelectedEstateGroup(null);
    setSelectedBuilding(null);
    setSelectedEstate(null);
    setSelectedUserType(null);
    setSelectedStatus(STATUS_OPTIONS[1]);
    setExpirationDate('');
    setEstateSearch('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canSubmit = !!selectedEstate?.id && !!selectedUserType?.id && !createMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedEstate || !selectedUserType) return;
    try {
      await createMutation.mutateAsync({
        user_id: userId,
        estate_id: selectedEstate.id,
        user_type_id: selectedUserType.id,
        status_id: selectedStatus.id,
        expiration_date: expirationDate || null,
      });
      handleClose();
    } catch {
      // toaster handled inside the mutation
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      size={DRAWER_SIZE.auto}
      anchor="right"
      overrides={{
        Root: { style: { zIndex: 10000 } },
        DrawerContainer: { style: { width: '480px', maxWidth: '90vw' } },
        DrawerBody: { style: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' } },
      }}
    >
      <div style={{ padding: '20px 24px 0 24px' }}>
        <HeadingMedium margin={0}>Add estate to user</HeadingMedium>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
          Grant this user a role on a property.
        </div>
      </div>

      <Body>
        <FormControlComponent label="Estate group *" caption="Pick the property group first">
          <SelectComponent
            size="compact"
            placeholder="Pick an estate group"
            options={estateGroupOptions}
            value={selectedEstateGroup ? [selectedEstateGroup] : []}
            isLoading={estateGroupsLoading}
            onChange={({ value }: any) => {
              setSelectedEstateGroup(value[0] || null);
              setSelectedBuilding(null); // reset building when group changes
              setSelectedEstate(null); // reset estate when group changes
              setEstateSearch('');
            }}
            clearable
            searchable
            overrides={selectPopoverOverrides}
          />
        </FormControlComponent>

        {selectedEstateGroup && buildingOptions.length > 0 && (
          <FormControlComponent
            label="Building"
            caption="Optional — filter estates by building"
          >
            <SelectComponent
              size="compact"
              placeholder="All buildings"
              options={buildingOptions}
              value={selectedBuilding ? [selectedBuilding] : []}
              isLoading={buildingsLoading}
              onChange={({ value }: any) => {
                setSelectedBuilding(value[0] || null);
                setSelectedEstate(null);
              }}
              clearable
              searchable
              overrides={selectPopoverOverrides}
            />
          </FormControlComponent>
        )}

        <FormControlComponent
          label="Estate *"
          caption={selectedEstateGroup ? 'Type to filter by name' : 'Pick an estate group first'}
        >
          <SelectComponent
            size="compact"
            placeholder="Search and pick an estate"
            options={estateOptions}
            value={selectedEstate ? [selectedEstate] : []}
            isLoading={estatesLoading}
            disabled={!selectedEstateGroup}
            onChange={({ value }: any) => setSelectedEstate(value[0] || null)}
            onInputChange={(e: any) => setEstateSearch(e?.target?.value || '')}
            clearable
            searchable
            filterOptions={(opts: any) => opts}
            overrides={selectPopoverOverrides}
          />
        </FormControlComponent>

        <FormControlComponent label="Role *" caption="The user_type for this user on this estate">
          <SelectComponent
            size="compact"
            placeholder="Pick a role"
            options={userTypeOptions}
            value={selectedUserType ? [selectedUserType] : []}
            isLoading={userTypesLoading}
            onChange={({ value }: any) => setSelectedUserType(value[0] || null)}
            clearable
            overrides={selectPopoverOverrides}
          />
        </FormControlComponent>

        <FormControlComponent label="Status" caption="Approved skips the approval flow; Pending requires another admin to approve">
          <SelectComponent
            size="compact"
            options={STATUS_OPTIONS}
            value={[selectedStatus]}
            onChange={({ value }: any) => setSelectedStatus(value[0] || STATUS_OPTIONS[1])}
            clearable={false}
            overrides={selectPopoverOverrides}
          />
        </FormControlComponent>

        <FormControlComponent label="Expiration date" caption="Optional — leave empty for no expiration">
          <DateField ariaLabel="Expiration date" value={expirationDate} onChange={setExpirationDate} />
        </FormControlComponent>
      </Body>

      <Footer>
        <Button kind={KIND.secondary} size={SIZE.compact} onClick={handleClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          size={SIZE.compact}
          onClick={handleSubmit}
          isLoading={createMutation.isPending}
          disabled={!canSubmit}
          overrides={{ BaseButton: { style: { backgroundColor: 'var(--tenant-primary, #111827)' } } }}
        >
          Add estate
        </Button>
      </Footer>
    </DrawerComponent>
  );
};
